import React, { useEffect, useState } from "react";
import {
  getPricing,
  updatePricingOptions,
} from "../../../services/AdminService";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
const PricingManager = () => {
  const apiInstance = useAxiosInstance();
  const [athletePricing, setAthletePricing] = useState({
    role: 1,
    annualPrice: "0.00",
    monthlyPrice: "0.00",
    combinePrice: "0.00",
    earlyCombinePrice: "0.00",
    dayOfCombinePrice: "0.00",
    combinePosDrillPrice: "0.00",
  });
  const [universityPricing, setUniversityPricing] = useState({
    role: 2,
    annualPrice: "0.00",
    monthlyPrice: "0.00",
    combinePrice: "0.00",
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPricingOptions = async () => {
      setLoading(true);
      try {
        const data = await getPricing(apiInstance);
        if (Array.isArray(data)) {
          const athleteData = data.find((option) => option.role === "Athlete");
          const universityData = data.find(
            (option) => option.role === "University",
          );
          if (athleteData) {
            setAthletePricing({
              role: 1,
              annualPrice: athleteData.pricingOptions.annualPrice.toFixed(2),
              monthlyPrice: athleteData.pricingOptions.monthlyPrice.toFixed(2),
              combinePrice: athleteData.pricingOptions.combinePrice.toFixed(2),
              earlyCombinePrice:
                athleteData.pricingOptions.earlyCombinePrice.toFixed(2),
              dayOfCombinePrice:
                athleteData.pricingOptions.dayOfCombinePrice.toFixed(2),
              combinePosDrillPrice:
                athleteData.pricingOptions.combinePosDrillPrice,
            });
          }
          if (universityData) {
            setUniversityPricing({
              role: 2,
              annualPrice: universityData.pricingOptions.annualPrice.toFixed(2),
              monthlyPrice:
                universityData.pricingOptions.monthlyPrice.toFixed(2),
              combinePrice:
                universityData.pricingOptions.combinePrice.toFixed(2),
            });
          }
        }
      } catch (error) {
        setError(error.message || "Error getting pricing options.");
      } finally {
        setLoading(false);
      }
    };

    fetchPricingOptions();
  }, [apiInstance]);

  const handleUpdatePricing = async () => {
    const pricingOptions = [
      {
        role: 1,
        annualPrice: athletePricing.annualPrice,
        monthlyPrice: athletePricing.monthlyPrice,
        combinePrice: athletePricing.combinePrice,
        earlyCombinePrice: athletePricing.earlyCombinePrice,
        dayOfCombinePrice: athletePricing.dayOfCombinePrice,
        combinePosDrillPrice: athletePricing.combinePosDrillPrice,
      },
      {
        role: 2,
        annualPrice: universityPricing.annualPrice,
        monthlyPrice: universityPricing.monthlyPrice,
        combinePrice: universityPricing.combinePrice,
      },
    ];
    try {
      await updatePricingOptions(apiInstance, pricingOptions);

      toast.success("Pricing updated successfully!");
    } catch (error) {
      setError("Failed to update pricing options.");
      toast.error("Failed to update pricing options.");
    }
  };

  const handleChange = (setFunction, field, value) => {
    const valid = /^(\d+(\.\d{0,2})?)?$/.test(value);
    if (valid || value === "") {
      setFunction((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  if (loading) {
    return <div className="hidden">Loading...</div>;
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className="flex flex-col gap-y-4 w-full">
      {/* Athlete Pricing Start */}
      <div className="bg-white shadow-lg rounded-lg py-4 border-gray-300">
        <div className="w-full px-4">
          <h1 className="text-xl mb-4 uppercase">Athlete Pricing</h1>
          <div className="my-6">
            <form className="mt-4 space-y-4 lg:mt-5 md:space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-medium text-gray-900">
                    Annual Subscription Amount
                  </label>
                  <input
                    type="text"
                    value={athletePricing?.annualPrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "annualPrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label className="font-medium text-gray-900">
                    Monthly Subscription Amount
                  </label>
                  <input
                    type="text"
                    value={athletePricing?.monthlyPrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "monthlyPrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label
                    htmlFor="earlyCombinePrice"
                    className="font-medium text-gray-900"
                  >
                    Early Combine Price
                  </label>
                  <input
                    type="text"
                    id="earlyCombinePrice"
                    name="earlyCombinePrice"
                    value={athletePricing?.earlyCombinePrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "earlyCombinePrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label
                    htmlFor="combinePrice"
                    className="font-medium text-gray-900"
                  >
                    Two Week Combine Price
                  </label>
                  <input
                    type="text"
                    id="combinePrice"
                    name="combinePrice"
                    value={athletePricing?.combinePrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "combinePrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>

                <div>
                  <label
                    htmlFor="dayOfCombinePrice"
                    className="font-medium text-gray-900"
                  >
                    Day of Combine Price
                  </label>
                  <input
                    type="text"
                    id="dayOfCombinePrice"
                    name="dayOfCombinePrice"
                    value={athletePricing?.dayOfCombinePrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "dayOfCombinePrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label
                    htmlFor="combinePosDrillPrice"
                    className="font-medium text-gray-900"
                  >
                    Position Drill Price
                  </label>
                  <input
                    type="text"
                    id="combinePosDrillPrice"
                    name="combinePosDrillPrice"
                    value={athletePricing?.combinePosDrillPrice}
                    onChange={(e) =>
                      handleChange(
                        setAthletePricing,
                        "combinePosDrillPrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* University Pricing Start */}
      <div className="bg-white shadow-lg rounded-lg py-4 border-gray-300">
        <div className="w-full px-4">
          <h1 className="text-xl mb-4 uppercase">University Pricing</h1>
          <div className="my-6">
            <form className="mt-4 space-y-4 lg:mt-5 md:space-y-5">
              <div className="grid lg:grid-cols-2 xl:grid-cols-2 gap-6">
                <div>
                  <label className="font-medium text-gray-900">
                    Annual Subscription Amount
                  </label>
                  <input
                    type="text"
                    value={universityPricing?.annualPrice}
                    onChange={(e) =>
                      handleChange(
                        setUniversityPricing,
                        "annualPrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
                <div>
                  <label className="font-medium text-gray-900">
                    Monthly Subscription Amount
                  </label>
                  <input
                    type="text"
                    value={universityPricing?.monthlyPrice}
                    onChange={(e) =>
                      handleChange(
                        setUniversityPricing,
                        "monthlyPrice",
                        e.target.value,
                      )
                    }
                    className="block w-full mt-2 rounded-md border-0 py-3 pl-4 pr-2 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-600"
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
        <div className="text-center">
          <button
            type="button"
            className="bg-red-500 text-white uppercase rounded px-6 py-2 text-center hover:bg-red-700 cursor-pointer mt-2"
            onClick={handleUpdatePricing}
          >
            Update All Pricing
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingManager;
